import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Power, Mail, MessageSquare, Settings, Users, TrendingUp,
  CheckCircle, Clock, XCircle, Send, Eye, RefreshCw, ToggleLeft, ToggleRight
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface SystemSettings {
  enabled: boolean;
  loading: boolean;
}

interface ReviewRequest {
  id: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  status: string;
  scheduled_send_at: string;
  sent_at: string | null;
  email_sent: boolean;
  sms_sent: boolean;
  created_at: string;
  campaign: {
    name: string;
  };
}

interface Stats {
  total: number;
  pending: number;
  sent: number;
  failed: number;
}

const ReviewRequestsPage: React.FC = () => {
  const { theme } = useTheme();
  const [masterSwitch, setMasterSwitch] = useState<SystemSettings>({ enabled: false, loading: true });
  const [requests, setRequests] = useState<ReviewRequest[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, pending: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'sent' | 'failed'>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadMasterSwitch(),
      loadReviewRequests(),
      loadStats()
    ]);
  };

  const loadMasterSwitch = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'review_requests_enabled')
        .single();

      if (error) throw error;

      setMasterSwitch({
        enabled: data?.value === true,
        loading: false
      });
    } catch (err) {
      console.error('Error loading master switch:', err);
      setMasterSwitch({ enabled: false, loading: false });
    }
  };

  const loadReviewRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('review_requests')
        .select(`
          *,
          campaign:review_campaigns(name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      setRequests(data || []);
    } catch (err) {
      console.error('Error loading review requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .select('status');

      if (error) throw error;

      const total = data?.length || 0;
      const pending = data?.filter(r => r.status === 'pending').length || 0;
      const sent = data?.filter(r => r.status === 'sent').length || 0;
      const failed = data?.filter(r => r.status === 'failed').length || 0;

      setStats({ total, pending, sent, failed });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const toggleMasterSwitch = async () => {
    try {
      setMasterSwitch(prev => ({ ...prev, loading: true }));

      const newValue = !masterSwitch.enabled;

      const { error } = await supabase
        .from('system_settings')
        .update({ value: newValue })
        .eq('key', 'review_requests_enabled');

      if (error) throw error;

      setMasterSwitch({ enabled: newValue, loading: false });
    } catch (err) {
      console.error('Error toggling master switch:', err);
      setMasterSwitch(prev => ({ ...prev, loading: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': 
        return { bg: theme.colors.successBg, text: theme.colors.successText };
      case 'pending': 
        return { bg: theme.colors.warningBg, text: theme.colors.warningText };
      case 'failed': 
        return { bg: theme.colors.dangerBg, text: theme.colors.dangerText };
      default: 
        return { bg: theme.colors.bgHover, text: theme.colors.textSecondary };
    }
  };

  const filteredRequests = requests.filter(req => {
    if (activeTab === 'all') return true;
    return req.status === activeTab;
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: theme.colors.textPrimary, marginBottom: '0.5rem' }}>
              Review Requests
            </h1>
            <p style={{ color: theme.colors.textSecondary }}>Manage automated review request campaigns</p>
          </div>
          <button
            onClick={loadData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              border: `1px solid ${theme.colors.borderColor}`,
              borderRadius: '0.5rem',
              backgroundColor: 'transparent',
              color: theme.colors.textPrimary,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <RefreshCw style={{ height: '1rem', width: '1rem' }} />
            Refresh
          </button>
        </div>

        {/* Master Switch */}
        <div style={{ 
          backgroundColor: theme.colors.bgCard, 
          border: `1px solid ${theme.colors.borderColor}`, 
          borderRadius: '0.5rem',
          marginBottom: '1.5rem' 
        }}>
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem',
                  backgroundColor: masterSwitch.enabled ? theme.colors.successBg : theme.colors.dangerBg
                }}>
                  <Power style={{ 
                    height: '1.5rem', 
                    width: '1.5rem',
                    color: masterSwitch.enabled ? theme.colors.successText : theme.colors.dangerText
                  }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '0.25rem' }}>
                    Review Requests {masterSwitch.enabled ? 'Enabled' : 'Disabled'}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: theme.colors.textSecondary }}>
                    {masterSwitch.enabled 
                      ? 'Emails and SMS are being sent to customers automatically'
                      : 'Review requests are created but NOT sent (test mode)'}
                  </p>
                </div>
              </div>
              <button
                onClick={toggleMasterSwitch}
                disabled={masterSwitch.loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  backgroundColor: masterSwitch.enabled ? theme.colors.dangerBg : theme.colors.successBg,
                  color: masterSwitch.enabled ? theme.colors.dangerText : theme.colors.successText,
                  border: 'none',
                  cursor: masterSwitch.loading ? 'not-allowed' : 'pointer',
                  opacity: masterSwitch.loading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!masterSwitch.loading) e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  if (!masterSwitch.loading) e.currentTarget.style.opacity = '1';
                }}
              >
                {masterSwitch.loading ? (
                  <RefreshCw style={{ height: '1.25rem', width: '1.25rem', animation: 'spin 1s linear infinite' }} />
                ) : masterSwitch.enabled ? (
                  <>
                    <ToggleRight style={{ height: '1.25rem', width: '1.25rem' }} />
                    Disable Sending
                  </>
                ) : (
                  <>
                    <ToggleLeft style={{ height: '1.25rem', width: '1.25rem' }} />
                    Enable Sending
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: theme.colors.textSecondary }}>Total Requests</span>
                <Users style={{ height: '1.25rem', width: '1.25rem', color: theme.colors.primaryColor }} />
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: '700', color: theme.colors.textPrimary }}>{stats.total}</div>
            </div>
          </div>

          <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: theme.colors.textSecondary }}>Pending</span>
                <Clock style={{ height: '1.25rem', width: '1.25rem', color: theme.colors.warningText }} />
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: '700', color: theme.colors.warningText }}>{stats.pending}</div>
            </div>
          </div>

          <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: theme.colors.textSecondary }}>Sent</span>
                <CheckCircle style={{ height: '1.25rem', width: '1.25rem', color: theme.colors.successText }} />
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: '700', color: theme.colors.successText }}>{stats.sent}</div>
            </div>
          </div>

          <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: theme.colors.textSecondary }}>Failed</span>
                <XCircle style={{ height: '1.25rem', width: '1.25rem', color: theme.colors.dangerText }} />
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: '700', color: theme.colors.dangerText }}>{stats.failed}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {[
            { key: 'all', label: 'All Requests' },
            { key: 'pending', label: 'Pending' },
            { key: 'sent', label: 'Sent' },
            { key: 'failed', label: 'Failed' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                backgroundColor: activeTab === tab.key ? theme.colors.primaryColor : theme.colors.bgCard,
                color: activeTab === tab.key ? 'white' : theme.colors.textSecondary,
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.key) e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.key) e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {loading ? (
            <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <RefreshCw style={{ 
                  height: '2rem', 
                  width: '2rem', 
                  color: theme.colors.primaryColor, 
                  margin: '0 auto 1rem',
                  animation: 'spin 1s linear infinite' 
                }} />
                <p style={{ color: theme.colors.textSecondary }}>Loading review requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}>
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <Mail style={{ height: '3rem', width: '3rem', color: theme.colors.textTertiary, margin: '0 auto 1rem' }} />
                <p style={{ color: theme.colors.textPrimary, marginBottom: '0.5rem' }}>No review requests found</p>
                <p style={{ fontSize: '0.875rem', color: theme.colors.textSecondary }}>
                  Review requests will be created automatically when SuccessWare jobs are completed
                </p>
              </div>
            </div>
          ) : (
            filteredRequests.map(request => {
              const statusColor = getStatusColor(request.status);
              return (
                <div 
                  key={request.id} 
                  style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '0.5rem' }}
                >
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '0.5rem' }}>
                          {request.customer_name}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem', color: theme.colors.textSecondary }}>
                          {request.customer_email && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <Mail style={{ height: '1rem', width: '1rem' }} />
                              {request.customer_email}
                              {request.email_sent && <CheckCircle style={{ height: '1rem', width: '1rem', color: theme.colors.successText, marginLeft: '0.25rem' }} />}
                            </div>
                          )}
                          {request.customer_phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <MessageSquare style={{ height: '1rem', width: '1rem' }} />
                              {request.customer_phone}
                              {request.sms_sent && <CheckCircle style={{ height: '1rem', width: '1rem', color: theme.colors.successText, marginLeft: '0.25rem' }} />}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ 
                          padding: '0.25rem 0.75rem', 
                          fontSize: '0.875rem', 
                          fontWeight: '500', 
                          borderRadius: '0.25rem',
                          backgroundColor: statusColor.bg,
                          color: statusColor.text
                        }}>
                          {request.status}
                        </span>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      fontSize: '0.875rem', 
                      color: theme.colors.textSecondary, 
                      paddingTop: '1rem', 
                      borderTop: `1px solid ${theme.colors.borderColor}` 
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span>Campaign: {request.campaign?.name || 'Unknown'}</span>
                        <span>Scheduled: {new Date(request.scheduled_send_at).toLocaleString()}</span>
                        {request.sent_at && (
                          <span>Sent: {new Date(request.sent_at).toLocaleString()}</span>
                        )}
                      </div>
                      <button 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.25rem',
                          background: 'transparent',
                          border: 'none',
                          color: theme.colors.textSecondary,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.textPrimary}
                        onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
                      >
                        <Eye style={{ height: '1rem', width: '1rem' }} />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewRequestsPage;
